'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useDebounce } from './useDebounce';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export type UseAutosaveMutationOptions<T> = {
  /** The latest locally edited value. */
  value: T;
  /** The last value confirmed by the server or parent state. */
  persistedValue: T;
  /**
   * Identifies the resource and field being saved. Changing this key resets
   * failed/successful value tracking, for example when switching records.
   */
  contextKey: string;
  /**
   * Persists a value. It must reject when the request fails so the hook can
   * block that exact value from being submitted repeatedly.
   */
  onSave: (value: T) => Promise<void>;
  /** Debounce duration in milliseconds. Defaults to 300. */
  delay?: number;
  /** Disables automatic saving while false. Defaults to true. */
  enabled?: boolean;
  /** Receives the error from a rejected save once per attempted value. */
  onError?: (error: unknown, value: T) => void;
  /** Runs after a value is saved successfully. */
  onSuccess?: (value: T) => void;
  /** Value comparator. Defaults to Object.is. */
  isEqual?: (left: T, right: T) => boolean;
};

export type UseAutosaveMutationResult = {
  status: AutosaveStatus;
  error: unknown;
  /** Explicitly retries the currently failed value. */
  retry: () => void;
};

type TrackedValue<T> = {
  contextKey: string;
  value: T;
};

type AutosaveResult = {
  contextKey: string;
  status: AutosaveStatus;
  error: unknown;
};

/**
 * Debounces and coordinates an autosave mutation without retrying a rejected
 * value on subsequent renders.
 *
 * Only one save runs at a time. Edits made during an in-flight request are
 * coalesced, and the latest value is considered when that request settles. A
 * rejected value remains blocked until the value or `contextKey` changes, or
 * the consumer calls `retry`. This makes the hook suitable for autosave UIs
 * where render cycles, cache invalidation, or mutation state changes must not
 * turn one validation error into a request loop.
 *
 * `onSave` must reject on failure. Consumers can use `status` and `error` for
 * inline feedback and `onError` for notifications or monitoring.
 */
export function useAutosaveMutation<T>({
  value,
  persistedValue,
  contextKey,
  onSave,
  delay = 300,
  enabled = true,
  onError,
  onSuccess,
  isEqual = Object.is,
}: UseAutosaveMutationOptions<T>): UseAutosaveMutationResult {
  const debouncedValue = useDebounce(value, delay);
  const [result, setResult] = useState<AutosaveResult>({
    contextKey,
    status: 'idle',
    error: null,
  });
  const [revision, setRevision] = useState(0);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const generationRef = useRef(0);
  const failedRef = useRef<TrackedValue<T> | null>(null);
  const succeededRef = useRef<TrackedValue<T> | null>(null);
  const previousContextKeyRef = useRef(contextKey);
  const onSaveRef = useRef(onSave);
  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);
  const isEqualRef = useRef(isEqual);

  useEffect(() => {
    onSaveRef.current = onSave;
    onErrorRef.current = onError;
    onSuccessRef.current = onSuccess;
    isEqualRef.current = isEqual;
  }, [isEqual, onError, onSave, onSuccess]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (previousContextKeyRef.current === contextKey) return;

    previousContextKeyRef.current = contextKey;
    generationRef.current += 1;
    failedRef.current = null;
    succeededRef.current = null;
  }, [contextKey]);

  useEffect(() => {
    if (!enabled || isEqualRef.current(debouncedValue, persistedValue)) return;

    const matchesTrackedValue = (tracked: TrackedValue<T> | null) =>
      tracked?.contextKey === contextKey &&
      isEqualRef.current(tracked.value, debouncedValue);

    if (
      inFlightRef.current ||
      matchesTrackedValue(failedRef.current) ||
      matchesTrackedValue(succeededRef.current)
    ) {
      return;
    }

    const valueToSave = debouncedValue;
    const requestGeneration = generationRef.current;
    inFlightRef.current = true;

    void Promise.resolve()
      .then(() => {
        if (
          !mountedRef.current ||
          requestGeneration !== generationRef.current
        ) {
          return;
        }

        setResult({ contextKey, status: 'saving', error: null });
        return onSaveRef.current(valueToSave);
      })
      .then(() => {
        if (
          !mountedRef.current ||
          requestGeneration !== generationRef.current
        ) {
          return;
        }

        failedRef.current = null;
        succeededRef.current = { contextKey, value: valueToSave };
        setResult({ contextKey, status: 'saved', error: null });
        onSuccessRef.current?.(valueToSave);
      })
      .catch((saveError: unknown) => {
        if (
          !mountedRef.current ||
          requestGeneration !== generationRef.current
        ) {
          return;
        }

        failedRef.current = { contextKey, value: valueToSave };
        setResult({ contextKey, status: 'error', error: saveError });
        onErrorRef.current?.(saveError, valueToSave);
      })
      .finally(() => {
        inFlightRef.current = false;
        if (mountedRef.current) {
          setRevision((current) => current + 1);
        }
      });
  }, [contextKey, debouncedValue, enabled, persistedValue, revision]);

  const retry = useCallback(() => {
    failedRef.current = null;
    setResult({ contextKey, status: 'idle', error: null });
    setRevision((current) => current + 1);
  }, [contextKey]);

  return result.contextKey === contextKey
    ? { status: result.status, error: result.error, retry }
    : { status: 'idle', error: null, retry };
}
